import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { PhotoDetail } from '@/types/photo';
import { getPhotosByAlbumId, searchPhotos } from '@/services/photoService';
import { getAlbumById } from '@/services/albumService';
import { createWarmMusicPlayer, type WarmMusicPlayer } from './warmMusic';
import SlideStage from './SlideStage';
import { decodeSlideImage, slideImageSource } from './slideMedia';

const SLIDE_INTERVAL_OPTIONS = [1000, 3000, 5000] as const;
const DEFAULT_SLIDE_INTERVAL_MS = 3000;
const CONTROLS_HIDE_DELAY_MS = 3500;
const SWIPE_DISTANCE_PX = 48;

function getIntervalLabel(interval: number) {
  return `${interval / 1000}초`;
}

function shufflePhotos(items: PhotoDetail[]) {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const target = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[target]] = [shuffled[target], shuffled[index]];
  }
  return shuffled;
}

export default function SlideshowPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const albumId = params.get('albumId') || '';
  const [photos, setPhotos] = useState<PhotoDetail[]>([]);
  const [albumTitle, setAlbumTitle] = useState('사진 슬라이드쇼');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentIndexRef = useRef(0);
  const [transitionSequence, setTransitionSequence] = useState(0);
  const [isVisible, setIsVisible] = useState(!document.hidden);
  const [transitionDirection, setTransitionDirection] = useState(1);
  const [slideReady, setSlideReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [slideInterval, setSlideInterval] = useState(DEFAULT_SLIDE_INTERVAL_MS);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [musicEnabled, setMusicEnabled] = useState(true);
  const [musicTitle, setMusicTitle] = useState('따뜻한 연주곡');
  const touchStartXRef = useRef<number | null>(null);
  const musicPlayerRef = useRef<WarmMusicPlayer | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const suppressClickUntilRef = useRef(0);
  const musicEnabledRef = useRef(true);
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;

  const stopMusic = useCallback(() => {
    musicPlayerRef.current?.stop();
    musicPlayerRef.current = null;
  }, []);

  const startMusic = useCallback(() => {
    if (!musicEnabledRef.current || !isPlayingRef.current || document.hidden) return;
    if (!musicPlayerRef.current) {
      musicPlayerRef.current = createWarmMusicPlayer(setMusicTitle);
    }
    musicPlayerRef.current?.resume();
  }, []);

  useEffect(() => {
    const unlock = () => startMusic();
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [startMusic]);

  useEffect(() => {
    if (!isPlaying || !musicEnabled || !isVisible) musicPlayerRef.current?.pause();
    else musicPlayerRef.current?.resume();
  }, [isPlaying, musicEnabled, isVisible]);

  useEffect(() => {
    const update = () => setIsVisible(!document.hidden);
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  useEffect(() => () => stopMusic(), [stopMusic]);

  const close = useCallback(() => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/albums');
  }, [navigate]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      setCurrentIndex(0);
      currentIndexRef.current = 0;
      setTransitionSequence(0);
      setSlideReady(false);
      try {
        if (albumId) {
          const [album, albumPhotos] = await Promise.all([
            getAlbumById(albumId),
            getPhotosByAlbumId(albumId),
          ]);
          if (!active) return;
          const nextTitle = album?.title || '앨범 슬라이드쇼';
          setAlbumTitle(nextTitle);
          setPhotos(shufflePhotos(albumPhotos));
        } else {
          const searched = await searchPhotos({
            query: params.get('q') || undefined,
            shotDate: params.get('shotDate') || undefined,
            favoriteOnly: params.get('favoriteOnly') === 'true',
            limit: 100,
          });
          if (!active) return;
          const nextTitle = params.get('favoriteOnly') === 'true' ? '즐겨찾기 슬라이드쇼' : '사진 슬라이드쇼';
          setAlbumTitle(nextTitle);
          setPhotos(shufflePhotos(searched));
        }
      } catch {
        if (active) setError('슬라이드쇼 사진을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [albumId, params]);

  const currentPhoto = photos[currentIndex];

  const changeSlide = useCallback((index: number, direction: 1 | -1, revealControls = true) => {
    if (!photos[index] || index === currentIndexRef.current) return;
    setTransitionSequence((value) => value + 1);
    setTransitionDirection(direction);
    setSlideReady(false);
    currentIndexRef.current = index;
    setCurrentIndex(index);
    if (revealControls) setShowControls(true);
  }, [photos]);

  const move = useCallback((direction: 1 | -1, revealControls = true) => {
    if (!photos.length) return;
    changeSlide((currentIndexRef.current + direction + photos.length) % photos.length, direction, revealControls);
  }, [changeSlide, photos.length]);

  const selectPhoto = useCallback((index: number) => {
    changeSlide(index, index >= currentIndexRef.current ? 1 : -1);
  }, [changeSlide]);

  useEffect(() => {
    if (!slideReady || !isPlaying || !isVisible || photos.length < 2 || currentPhoto?.mediaType === 'video') return undefined;
    const timer = window.setTimeout(() => move(1, false), slideInterval);
    return () => window.clearTimeout(timer);
  }, [slideReady, currentPhoto?.id, currentPhoto?.mediaType, isPlaying, isVisible, move, photos.length, slideInterval]);

  // 자동 재생 중에는 조작 안내를 잠시만 보여 주고 사진 감상 화면을 깨끗하게 유지합니다.
  useEffect(() => {
    if (!showControls || !isPlaying) return undefined;
    const timer = window.setTimeout(() => setShowControls(false), CONTROLS_HIDE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [isPlaying, showControls]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') move(-1);
      if (event.key === 'ArrowRight') move(1);
      if (event.key === ' ') {
        event.preventDefault();
        setIsPlaying((playing) => !playing);
        setShowControls(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [close, move]);

  // 다음·이전 사진은 미리 받아 두어 넘길 때 화면이 최대한 매끄럽게 이어집니다.
  useEffect(() => {
    if (photos.length < 2) return;
    [1, -1].forEach((offset) => {
      const photo = photos[(currentIndex + offset + photos.length) % photos.length];
      if (!photo || photo.mediaType === 'video') return;
      void decodeSlideImage(slideImageSource(photo)).catch(() => { /* The visible slide offers retry. */ });
    });
  }, [currentIndex, photos]);

  const previewIndices = useMemo(() => {
    const count = Math.min(7, photos.length);
    const start = Math.max(0, Math.min(currentIndex - 3, photos.length - count));
    return Array.from({ length: count }, (_, offset) => start + offset);
  }, [currentIndex, photos.length]);

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    const target = event.target instanceof Element ? event.target : null;
    if (event.touches.length !== 1 || target?.closest('button,input,select,a,[data-slide-controls]')) {
      touchStartXRef.current = null; touchStartYRef.current = null; return;
    }
    touchStartXRef.current = event.changedTouches[0]?.clientX ?? null;
    touchStartYRef.current = event.changedTouches[0]?.clientY ?? null;
  };
  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const x = touchStartXRef.current; const y = touchStartYRef.current; const touch = event.changedTouches[0];
    touchStartXRef.current = null; touchStartYRef.current = null;
    if (x == null || y == null || !touch) return;
    const dx = touch.clientX - x; const dy = touch.clientY - y;
    if (Math.abs(dx) < SWIPE_DISTANCE_PX || Math.abs(dx) < Math.abs(dy) * 1.25) return;
    suppressClickUntilRef.current = Date.now() + 500;
    move(dx < 0 ? 1 : -1);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-3 bg-foreground-950 text-background-50">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        <p className="text-sm text-white/65">슬라이드쇼를 준비하고 있어요</p>
      </div>
    );
  }

  if (error || !currentPhoto) {
    return (
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-foreground-950 px-6 text-center text-background-50">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10"><i className="ri-slideshow-line text-3xl text-white/60" /></div>
        <p className="mt-4 text-sm">{error || '보여 줄 사진이 없어요'}</p>
        <p className="mt-1 text-xs text-white/55">사진을 올리거나 검색 조건을 바꿔 보세요.</p>
        <button type="button" onClick={close} className="mt-5 rounded-full bg-white/15 px-5 py-2.5 text-sm font-medium">돌아가기</button>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] select-none overflow-hidden bg-black text-white"
      onClick={() => { if (Date.now() < suppressClickUntilRef.current) return; if (musicEnabled) startMusic(); setShowControls((visible) => !visible); }}
      onMouseMove={() => setShowControls(true)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={() => { touchStartXRef.current = null; touchStartYRef.current = null; }}
      style={{touchAction: 'pan-y pinch-zoom'}}
    >
      <SlideStage photo={currentPhoto} playing={isPlaying && isVisible} muted={isMuted}
        direction={transitionDirection} sequence={transitionSequence} onReady={setSlideReady}
        onEnded={() => { if (isPlayingRef.current) move(1, false); }} />

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/70" />

      {/* 자동 재생 시간에 맞춘 진행 표시 */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-1 bg-white/20">
        <div
          key={`${currentPhoto.id}-${slideInterval}-${isPlaying}`}
          className="h-full origin-left bg-white/90"
          style={{
            animation: slideReady && isPlaying && isVisible && currentPhoto.mediaType !== 'video' && photos.length > 1 ? `slideshowProgress ${slideInterval}ms linear forwards` : 'none',
          }}
        />
      </div>

      <button
        type="button"
        aria-label="이전 사진"
        onClick={(event) => { event.stopPropagation(); move(-1); }}
        className={`absolute left-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white sm:backdrop-blur-sm transition-all hover:bg-black/55 ${showControls ? 'translate-x-0 opacity-100' : '-translate-x-3 opacity-0 pointer-events-none'}`}
      >
        <i className="ri-arrow-left-s-line text-3xl" />
      </button>
      <button
        type="button"
        aria-label="다음 사진"
        onClick={(event) => { event.stopPropagation(); move(1); }}
        className={`absolute right-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white sm:backdrop-blur-sm transition-all hover:bg-black/55 ${showControls ? 'translate-x-0 opacity-100' : 'translate-x-3 opacity-0 pointer-events-none'}`}
      >
        <i className="ri-arrow-right-s-line text-3xl" />
      </button>

      <div className={`absolute inset-x-0 top-0 z-10 px-4 pt-[max(16px,env(safe-area-inset-top))] transition-all duration-300 ${showControls ? 'translate-y-0 opacity-100' : '-translate-y-3 opacity-0 pointer-events-none'}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="inline-flex max-w-full items-center gap-2 rounded-full bg-black/30 px-3 py-1.5 sm:backdrop-blur-sm">
              <i className="ri-slideshow-3-line text-sm text-white/80" />
              <p className="truncate text-sm font-semibold">{albumTitle}</p>
            </div>
            <p className="mt-2 pl-1 text-xs text-white/75">{currentPhoto.shotDate} · {currentIndex + 1} / {photos.length}</p>
          </div>
          <button type="button" onClick={(event) => { event.stopPropagation(); close(); }} className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-black/35 sm:backdrop-blur-sm transition-colors hover:bg-black/55" aria-label="슬라이드쇼 닫기"><i className="ri-close-line text-2xl" /></button>
        </div>
      </div>


      <div className={`absolute inset-x-0 bottom-0 z-20 px-3 pb-[max(16px,env(safe-area-inset-bottom))] transition-all duration-300 ${showControls ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0 pointer-events-none'}`}>
        <div data-slide-controls className="mx-auto max-w-3xl rounded-2xl border border-white/10 bg-black/45 px-3 py-3 shadow-2xl sm:backdrop-blur-xl sm:px-4">
          <div className="mb-3 flex items-center justify-center gap-2 overflow-x-auto px-0.5">
            {previewIndices.map((index) => {
              const photo = photos[index];
              const active = index === currentIndex;
              return (
                <button
                  key={photo.id}
                  type="button"
                  onClick={(event) => { event.stopPropagation(); selectPhoto(index); }}
                  className={`relative h-11 w-11 flex-shrink-0 overflow-hidden rounded-lg transition-all ${active ? 'scale-110 ring-2 ring-white ring-offset-2 ring-offset-black/60' : 'opacity-60 hover:opacity-100'}`}
                  aria-label={`${index + 1}번 사진 보기`}
                  aria-current={active ? 'true' : undefined}
                >
                  <img src={photo.image} alt="" className="h-full w-full object-cover" />
                  {photo.mediaType === 'video' && <span className="absolute inset-0 flex items-center justify-center bg-black/25"><i className="ri-play-fill text-sm" /></span>}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                const enabled = !musicEnabledRef.current;
                musicEnabledRef.current = enabled;
                setMusicEnabled(enabled);
                if (enabled) startMusic();
                else musicPlayerRef.current?.pause();
                setShowControls(true);
              }}
              className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors ${musicEnabled ? 'bg-accent-500 text-white' : 'bg-white/12 text-white/70 hover:bg-white/20'}`}
              aria-label={musicEnabled ? '배경 음악 끄기' : '배경 음악 켜기'}
              title={`${musicTitle} · 피아노·스트링·기타 연주`}
            >
              <i className={`${musicEnabled ? 'ri-music-2-fill' : 'ri-volume-mute-line'} text-lg`} />
            </button>
            <div className="flex items-center gap-2">
              <button type="button" onClick={(event) => { event.stopPropagation(); move(-1); }} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/12 transition-colors hover:bg-white/20" aria-label="이전 사진"><i className="ri-skip-left-fill text-lg" /></button>
              <button type="button" onClick={(event) => { event.stopPropagation(); setIsPlaying((playing) => !playing); setShowControls(true); }} className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-foreground-950 shadow-lg transition-transform hover:scale-105" aria-label={isPlaying ? '일시정지' : '재생'}><i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-xl`} /></button>
              <button type="button" onClick={(event) => { event.stopPropagation(); move(1); }} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/12 transition-colors hover:bg-white/20" aria-label="다음 사진"><i className="ri-skip-right-fill text-lg" /></button>
            </div>
            {currentPhoto.mediaType === 'video' ? (
              <button type="button" onClick={(event) => { event.stopPropagation(); setIsMuted((muted) => !muted); }} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/12 transition-colors hover:bg-white/20" aria-label={isMuted ? '동영상 소리 켜기' : '동영상 소리 끄기'}><i className={`${isMuted ? 'ri-volume-mute-line' : 'ri-volume-up-line'} text-lg`} /></button>
            ) : (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setSlideInterval((current) => {
                    const index = SLIDE_INTERVAL_OPTIONS.indexOf(current as (typeof SLIDE_INTERVAL_OPTIONS)[number]);
                    return SLIDE_INTERVAL_OPTIONS[(index + 1) % SLIDE_INTERVAL_OPTIONS.length];
                  });
                  setShowControls(true);
                }}
                className="flex h-10 min-w-10 items-center justify-center rounded-full bg-white/10 px-2 text-[11px] font-semibold text-white/90 transition-colors hover:bg-white/20"
                aria-label={`사진 넘김 시간 ${getIntervalLabel(slideInterval)}. 누르면 1초, 3초, 5초 순환`}
                title="사진 넘김 시간: 1초 → 3초 → 5초"
              >
                {getIntervalLabel(slideInterval)}
              </button>
            )}
          </div>

          {musicEnabled && (
            <div className="mt-3 flex items-center justify-center gap-2 text-xs text-white/65">
              <i className="ri-music-2-line" aria-hidden="true" />
              <span className="truncate">{musicTitle}</span>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  startMusic();
                  musicPlayerRef.current?.next();
                  setShowControls(true);
                }}
                className="rounded-full px-2 py-1 text-white/80 hover:bg-white/15"
                aria-label="다음 배경 음악"
                title="다른 연주 듣기"
              >다음 곡</button>
            </div>
          )}

          <p className="mt-3 truncate text-center text-xs font-medium text-white/90">{currentPhoto.caption || currentPhoto.originalFilename}</p>
        </div>
      </div>

      <style>{`
        @keyframes slideshowProgress {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
      `}</style>
    </div>
  );
}
